import React, { useRef } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { format } from 'date-fns';
import PropTypes from 'prop-types';

import API from '@/apiRequest';
import PendingOrdersComponent from '@/components/SchedulePage/Timeline/PendingOrders/PendingOrdersComponent';
import { useConfirmAlert } from '@/contexts/ConfirmAlertContext';
import {
  prependPendingOrder,
  deleteOrder,
  setUnsaved,
  clearFilteredInspectorsNotifications,
} from '@/store/ducks/schedule';
import { showToast } from '@/store/ducks/toasts';

const PendingOrdersContainer = ({ busy, error, layout, selectedMapOrderId, unlocatedOrderIds, onMapOrderClick }) => {
  const dispatch = useDispatch();
  const orders = useSelector(state => state.schedule.orders);
  const pendingOrders = useSelector(state => state.schedule.filteredPendingOrders);

  const orderRef = useRef();
  const intl = useIntl();

  const { openConfirmAlert } = useConfirmAlert();

  const unassign = data => {
    openConfirmAlert({
      message: intl.formatMessage({ id: 'WANT_UNASSIGN_ORDER_MESSAGE' }),
      onConfirm: async () => {
        const me = JSON.parse(window.sessionStorage.getItem('me'));
        const orderPublicId = data?.order?.public_id || data?.order?.id;
        const mappedOrder = {
          ...data?.order,
          id: orderPublicId,
          public_id: orderPublicId,
          identifier: data?.order?.building_id || data?.order?.identifier,
          credits:
            data?.order?.credits ??
            (data?.order?.price ? Math.round(Number(data.order.price) * 100) : undefined),
          area: data?.order?.area ? String(data?.order?.area) : '',
        };

        dispatch(deleteOrder(orderPublicId));
        dispatch(
          prependPendingOrder({
            client: mappedOrder?.client,
            code: mappedOrder?.code,
            identifier: mappedOrder?.identifier,
            id: orderPublicId,
            public_id: orderPublicId,
            type: mappedOrder?.type,
            credits: mappedOrder?.credits,
            address_id: mappedOrder?.address_id,
            order_type_id: mappedOrder?.order_type_id,
            building_type: mappedOrder?.building_type,
            allowed_at_date: mappedOrder?.allowed_at_date,
            accompanied_inspection: mappedOrder?.accompanied_inspection,
            search_tokens: mappedOrder?.search_tokens,
            furnished: mappedOrder?.furnished,
            modality: mappedOrder?.modality,
            urgency: mappedOrder?.urgency,
            created_at: mappedOrder?.created_at,
            access_information: mappedOrder?.access_information,
            details: mappedOrder?.details,
            status: mappedOrder?.status,
            city: mappedOrder?.city,
            neighborhood: mappedOrder?.neighborhood,
            street: mappedOrder?.street,
            number: mappedOrder?.number,
            complement: mappedOrder?.complement,
            state: mappedOrder?.state,
            zipcode: mappedOrder?.zipcode,
          })
        );
        dispatch(setUnsaved(true));
        dispatch(
          showToast({
            type: 'success',
            title: intl.formatMessage({ id: 'SUCCESS' }),
            text: intl.formatMessage({
              id: 'INSPECTOR_ORDER_UNASSIGNED_SUCCESS',
            }),
            duration: 3000,
          })
        );

        try {
          await API.post('/scheduler/sync', {
            channel: `panel_${me.panelId}`,
            event: 'SCHEDULE.ORDER_UNASSIGNED',
            data: {
              order: mappedOrder,
              schedule: data?.schedule,
              sender: {
                id: me?.uniqueId,
                who: me?.name,
                when: format(new Date(), 'yyyy-MM-dd HH:mm'),
              },
            },
          });
        } catch {
          dispatch(
            showToast({
              type: 'error',
              title: intl.formatMessage({ id: 'ERROR' }),
              text: intl.formatMessage({ id: 'UNABLE_SAVE_CALENDAR_DATA_ERROR_MESSAGE' }),
              duration: 3000,
            })
          );
        }
      },
    });
  };

  const handleDragOver = evt => {
    evt.preventDefault();
    return false;
  };

  const handleDrop = evt => {
    const data = JSON.parse(evt.dataTransfer.getData('application/json'));
    const orderPublicId = data?.order?.public_id || data?.order?.id;
    const isInTimeline = orders.some(order => order?.order?.public_id === orderPublicId);

    dispatch(clearFilteredInspectorsNotifications());

    if (!isInTimeline) return;
    unassign(data);
  };

  return (
    <PendingOrdersComponent
      pendingOrders={pendingOrders}
      busy={busy}
      error={error}
      orderRef={orderRef}
      handleDrop={handleDrop}
      handleDragOver={handleDragOver}
      intl={intl}
      layout={layout}
      selectedMapOrderId={selectedMapOrderId}
      unlocatedOrderIds={unlocatedOrderIds}
      onMapOrderClick={onMapOrderClick}
    />
  );
};

PendingOrdersContainer.propTypes = {
  busy: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  layout: PropTypes.oneOf(['timeline', 'map']),
  selectedMapOrderId: PropTypes.string,
  unlocatedOrderIds: PropTypes.arrayOf(PropTypes.string),
  onMapOrderClick: PropTypes.func,
};

PendingOrdersContainer.defaultProps = {
  layout: 'timeline',
  selectedMapOrderId: null,
  unlocatedOrderIds: [],
  onMapOrderClick: undefined,
};

export default PendingOrdersContainer;
